import { find, has, isEmpty, map, some } from "lodash";
import { Typography } from "PFComponents/typography";
import { useBulkOwnerCreate } from "PFCore/hooks/queries/owners";
import { Activity, Id, Profile } from "PFTypes";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import AddActivityProfiles from "../../../../activities/show/parts/add_activity_profiles";
import { useGrowl } from "../../../../use_growl";
import css from "../bulk_select_overlay.module.scss";

type BulkAddCoOwnerModalProps = {
  allItems: { id: Id; activity: Activity }[];
  selectedIds: number[];
  onClose: () => void;
  onSuccess: () => void;
};

export const BulkAddCoOwnerModal = ({
  allItems,
  selectedIds,
  onClose,
  onSuccess
}: BulkAddCoOwnerModalProps) => {
  const { t } = useTranslation("workflow");
  const growl = useGrowl();

  const { mutate: bulkAddCoOwner } = useBulkOwnerCreate(selectedIds, {
    onSuccess: (data) => {
      if (some(data, (entry) => has(entry, "errors"))) {
        growl({ message: t("parts.addCoOwnersError"), kind: "error" });
        return;
      }

      growl({ message: t("parts.addCoOwnerSuccess") });
      onSuccess();
    },
    onError: () => {
      growl({ message: t("parts.addCoOwnerError"), kind: "error" });
    }
  });

  const activityNames = useMemo(
    () => map(selectedIds, (id) => find(allItems, ["id", id])?.activity.name).join(", "),
    [selectedIds, allItems]
  );

  const [profiles, setProfiles] = useState<Profile[]>([]);

  const handleSubmit = () => {
    bulkAddCoOwner(map(profiles, "id"));
  };

  return (
    <AddActivityProfiles
      headerText={t("parts.addCoOwner")}
      submitButtonLabel={t("parts.addAndShare")}
      newProfiles={profiles}
      onNewProfilesChange={setProfiles}
      profiles={[]}
      onClose={onClose}
      onSubmit={handleSubmit}
      disableOKButton={isEmpty(profiles)}
    >
      <Typography className={css.bulkAddCoOwnerNote} variant="bodyRegular">
        {selectedIds.length < 6
          ? t("parts.addingCoOwnerToRoles", { roles: activityNames })
          : t("parts.addingCoOwnerToManyRoles", { count: selectedIds.length })}
      </Typography>
    </AddActivityProfiles>
  );
};
